import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ArrowUp, ArrowDown, Search } from "lucide-react";
import api from "@/utils/axios-utils";
import SpaceWelcome from "./SpaceWelcome";
import { useSpaceStore } from "../store/useSpaceStore";
import { useSpace } from "../hooks/useSpace";
import {
  LiveSpaceCard,
  DraftSpaceCard,
  ClosedSpaceCard,
  type SpaceSummary,
} from "./SpaceCards";
import { TeacherSpaceListSkeleton } from "./SpaceLoadingSkeletons";
import { EditSpaceModal } from "./EditSpaceModal";
import { useSpacesEventStream } from "../hooks/spacesEventStreamHook";
import type {
  SpacePresenceChangedPayload,
  SpaceCardUpdatedPayload,
} from "../hooks/spacesSSEEventPayloadTypes";

type ClosedSortBy = "date" | "name";

export default function TeacherSpaceList() {
  const navigate = useNavigate();
  const {
    closeSpace,
    relaunchSpace,
    publishSpace,
    deleteSpace,
    pauseSpace,
    resumeSpace,
  } = useSpace();
  const [spaces, setSpaces] = useState<SpaceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [closedSearch, setClosedSearch] = useState("");
  const [closedSortBy, setClosedSortBy] = useState<ClosedSortBy>("date");
  const [closedSortAsc, setClosedSortAsc] = useState(false);
  const [editingSpaceId, setEditingSpaceId] = useState<string | null>(null);
  const actionInFlight = useRef(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function runAction(label: string, operation: () => Promise<void>) {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setPendingAction(label);
    setActionError(null);
    try {
      await operation();
    } catch {
      setActionError(`Could not ${label.toLowerCase()}. Please try again.`);
    } finally {
      actionInFlight.current = false;
      setPendingAction(null);
    }
  }

  useEffect(() => {
    api
      .get<SpaceSummary[]>("/spaces")
      .then((res) => setSpaces(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const handleSpacePresenceChanged = useCallback(
    (payload: SpacePresenceChangedPayload) => {
      setSpaces((prev) =>
        prev.map((s) =>
          s.spaceId === payload.spaceId
            ? {
                ...s,
                connectedCount: payload.connectedCount,
                connectedParticipants: payload.participants,
              }
            : s,
        ),
      );
    },
    [],
  );

  const handleSpaceCardUpdated = useCallback(
    (payload: SpaceCardUpdatedPayload) => {
      setSpaces((prev) =>
        prev.map((s) =>
          s.spaceId === payload.spaceId
            ? {
                ...s,
                spaceName: payload.spaceName,
                description: payload.description,
                teacherName: payload.teacherName,
                totalEnrolled: payload.totalEnrolled,
                classroomIds: payload.classroomIds,
                classroomNames: payload.classroomNames,
                status: payload.status as SpaceSummary["status"],
                startedAt: payload.startedAt,
                createdAt: payload.createdAt,
              }
            : s,
        ),
      );
    },
    [],
  );

  useSpacesEventStream(!loading && !error, {
    onSpacePresenceChanged: handleSpacePresenceChanged,
    onSpaceCardUpdated: handleSpaceCardUpdated,
  });

  function handleEnter(space: SpaceSummary) {
    useSpaceStore
      .getState()
      .setSpace(space.spaceId, space.spaceName, space.lessonContext);
    navigate(`/spaces/${space.spaceId}`);
  }

  async function handleLaunch(spaceId: string) {
    const space = spaces.find((s) => s.spaceId === spaceId);
    if (!space || space.status !== "draft") return;
    await publishSpace(spaceId);
    navigate(`/spaces/${spaceId}`);
  }

  async function handleDelete(spaceId: string) {
    const space = spaces.find((s) => s.spaceId === spaceId);
    if (!space || space.status !== "draft") return;
    await deleteSpace(spaceId);
    setSpaces((prev) => prev.filter((s) => s.spaceId !== spaceId));
  }

  async function handlePause(spaceId: string) {
    await pauseSpace(spaceId);
    setSpaces((prev) =>
      prev.map((s) =>
        s.spaceId === spaceId ? { ...s, status: "paused" as const } : s,
      ),
    );
  }

  async function handleResume(spaceId: string) {
    await resumeSpace(spaceId);
    setSpaces((prev) =>
      prev.map((s) =>
        s.spaceId === spaceId ? { ...s, status: "open" as const } : s,
      ),
    );
  }

  async function handleRelaunch(spaceId: string) {
    await relaunchSpace(spaceId);
    setSpaces((prev) =>
      prev.map((s) =>
        s.spaceId === spaceId ? { ...s, status: "open" as const } : s,
      ),
    );
  }

  function handleEditSaved(updated: {
    spaceId: string;
    spaceName: string;
    description: string | null;
    lessonContext: string | null;
    classroomIds: number[];
  }) {
    setSpaces((prev) =>
      prev.map((s) =>
        s.spaceId === updated.spaceId
          ? {
              ...s,
              spaceName: updated.spaceName,
              description: updated.description,
              lessonContext: updated.lessonContext,
              classroomIds: updated.classroomIds,
            }
          : s,
      ),
    );
    setEditingSpaceId(null);
  }

  async function handleClose(spaceId: string) {
    try {
      await closeSpace(spaceId);
    } catch (err) {
      console.warn(
        "[TeacherSpaceList] closeSpace failed, retrying direct:",
        err,
      );
      try {
        await api.post(`/spaces/${spaceId}/close`);
      } catch (err2) {
        console.error("[TeacherSpaceList] close fallback also failed:", err2);
        throw err2;
      }
    }
    setSpaces((prev) =>
      prev.map((s) =>
        s.spaceId === spaceId ? { ...s, status: "closed" as const } : s,
      ),
    );
  }

  const activeSpaces = spaces.filter(
    (s) => s.status === "open" || s.status === "paused",
  );
  const draftSpaces = spaces.filter((s) => s.status === "draft");

  const closedSpaces = spaces
    .filter((s) => s.status === "closed")
    .filter(
      (s) =>
        !closedSearch ||
        s.spaceName.toLowerCase().includes(closedSearch.toLowerCase()),
    )
    .sort((a, b) => {
      let cmp = 0;
      if (closedSortBy === "name") {
        cmp = a.spaceName.localeCompare(b.spaceName);
      } else {
        const aDate = new Date(a.snapshotSavedAt ?? a.createdAt).getTime();
        const bDate = new Date(b.snapshotSavedAt ?? b.createdAt).getTime();
        cmp = aDate - bDate;
      }
      return closedSortAsc ? cmp : -cmp;
    });

  return (
    <div className="flex flex-col gap-8" aria-busy={pendingAction != null}>
      {editingSpaceId && (
        <EditSpaceModal
          spaceId={editingSpaceId}
          onClose={() => setEditingSpaceId(null)}
          onSaved={handleEditSaved}
        />
      )}

      <SpaceWelcome
        variant="teacher"
        onCreateSpace={() => navigate("/spaces/create")}
      />

      {loading && <TeacherSpaceListSkeleton />}
      {pendingAction && <p role="status">{pendingAction}…</p>}
      {actionError && (
        <p role="alert" className="text-sm text-red-700">
          {actionError}
        </p>
      )}
      {!loading && error && (
        <p className="text-sm text-red-400 font-mulish">
          Failed to load spaces. Please refresh.
        </p>
      )}

      {!loading && !error && spaces.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-white rounded-lg shadow-md p-8">
            <p className="text-gray-500 font-mulish">No spaces yet.</p>
            <button
              onClick={() => navigate("/spaces/create")}
              className="mt-4 text-[#28543D] text-lg font-semibold font-mulish"
            >
              + create new space
            </button>
          </div>
        </div>
      )}

      {!loading && activeSpaces.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-mulish font-bold text-darkgreen">
            Live now
          </h2>
          {activeSpaces.map((space) => (
            <LiveSpaceCard
              key={space.spaceId}
              space={space}
              onEnter={handleEnter}
              onClose={(id) => runAction("End Space", () => handleClose(id))}
              onPause={(id) => runAction("Pause Space", () => handlePause(id))}
              onResume={(id) =>
                runAction("Resume Space", () => handleResume(id))
              }
              onEdit={(spaceId) => setEditingSpaceId(spaceId)}
            />
          ))}
        </div>
      )}

      {!loading && draftSpaces.length > 0 && (
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-mulish font-bold text-gray-500">
            Draft Spaces
          </h2>
          {draftSpaces.map((space) => (
            <DraftSpaceCard
              key={space.spaceId}
              space={space}
              onEnter={handleEnter}
              onLaunch={(id) =>
                runAction("Launch Space", () => handleLaunch(id))
              }
              onDelete={(id) =>
                runAction("Delete draft", () => handleDelete(id))
              }
              onEdit={(spaceId) => setEditingSpaceId(spaceId)}
            />
          ))}
        </div>
      )}

      {!loading && spaces.some((s) => s.status === "closed") && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-2xl font-mulish font-bold text-gray-400">
              Closed Spaces
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-400 font-mulish shrink-0">
                Sorting by:
              </span>
              <div className="relative">
                <select
                  aria-label="Sort closed Spaces"
                  value={closedSortBy}
                  onChange={(e) =>
                    setClosedSortBy(e.target.value as ClosedSortBy)
                  }
                  className="appearance-none h-9 pl-3 pr-8 rounded-[6px] border border-gray-200 bg-white text-sm font-semibold font-mulish text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#28543D] cursor-pointer"
                >
                  <option value="date">Date</option>
                  <option value="name">Name</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              </div>
              <button
                onClick={() => setClosedSortAsc((v) => !v)}
                className="w-9 h-9 flex items-center justify-center rounded-[6px] border border-gray-200 bg-white text-[#28543D] hover:bg-gray-50 transition-colors"
                title={closedSortAsc ? "Ascending" : "Descending"}
              >
                {closedSortAsc ? (
                  <ArrowUp className="w-4 h-4" />
                ) : (
                  <ArrowDown className="w-4 h-4" />
                )}
              </button>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  aria-label="Search closed Spaces"
                  type="text"
                  value={closedSearch}
                  onChange={(e) => setClosedSearch(e.target.value)}
                  placeholder="Search spaces"
                  className="h-9 pl-8 pr-3 rounded-[6px] border border-gray-200 bg-white text-sm font-mulish text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#28543D] w-44"
                />
              </div>
            </div>
          </div>
          {closedSpaces.length === 0 ? (
            <p className="text-sm text-gray-400 font-mulish text-center py-4">
              No spaces match your search.
            </p>
          ) : (
            closedSpaces.map((space) => (
              <ClosedSpaceCard
                key={space.spaceId}
                space={space}
                onRelaunch={(id) =>
                  runAction("Relaunch Space", () => handleRelaunch(id))
                }
                onViewRecap={(spaceId) => navigate(`/spaces/${spaceId}/recap`)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}
